import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { MultiGroupFilter } from "components/Filters/MultiGroupFilter";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Button } from "components/DesignSystem/Button/Button";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetAdminMissingInfoQuery } from "store/apis/alarm";
import { MissingInfoResult } from "types/Models/deadlineAlarm";
import { useMemo, useState } from "react";
import { useDebounce } from "utils/debounce";

const MISSING_INFO_OPTIONS = [
  { label: "Date of Incorporation", value: "incorporation" },
  { label: "Foreign Qualification Date", value: "foreign_qualification" },
  { label: "Tax Year End", value: "tax_year_end" },
  { label: "Entity Taxed As", value: "entity_taxed_as" },
];

type MissingInfoRow = {
  entityUuid: string;
  entityName: string;
  groupUuid: string;
  groupName: string;
  missingFields: string;
  showGroupName: boolean;
};

const columnHelper = createColumnHelper<MissingInfoRow>();

const buildColumns = (onAddMissingInfo: (row: MissingInfoRow) => void) => [
  columnHelper.accessor("groupName", {
    header: "Group",
    cell: ({ row }) =>
      row.original.showGroupName ? (
        <span className="t-text-body-sm t-text-text-60">
          {row.original.groupName}
        </span>
      ) : null,
  }),
  columnHelper.accessor("entityName", {
    header: "Entity",
    cell: ({ row }) => (
      <span className="t-text-body-sm">{row.original.entityName}</span>
    ),
  }),
  columnHelper.accessor("missingFields", {
    header: "Missing Info",
    cell: ({ row }) => (
      <span className="t-text-body-sm t-text-text-60">
        {row.original.missingFields}
      </span>
    ),
  }),
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <Button
        customType="secondary"
        size="small"
        onClick={() => onAddMissingInfo(row.original)}
      >
        Add missing info
      </Button>
    ),
  }),
];

const buildTableData = (results: MissingInfoResult[]): MissingInfoRow[] => {
  const seenGroups = new Set<string>();
  return results.map((item) => {
    const showGroupName = !seenGroups.has(item.group_uuid);
    seenGroups.add(item.group_uuid);
    return {
      entityUuid: item.entity_uuid,
      entityName: item.entity_name,
      groupUuid: item.group_uuid,
      groupName: item.group_name,
      missingFields: item.missing_fields.join(", "),
      showGroupName,
    };
  });
};

type FilterValues = {
  GROUPS: string[];
  MISSING_INFO_TYPE: string[];
};

export const AdminMissingInfo = () => {
  const [searchInput, setSearchInput] = useState("");
  const searchTerm = useDebounce(searchInput, 300);
  const [pageNum, setPageNum] = useState(1);
  const [filterValues, setFilterValues] = useState<FilterValues>({
    GROUPS: [],
    MISSING_INFO_TYPE: [],
  });

  const updateFilter = <K extends keyof FilterValues>(
    key: K,
    value: FilterValues[K]
  ) => {
    setPageNum(1);
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const { data, isLoading } = useGetAdminMissingInfoQuery(
    {
      searchTerm: searchTerm || undefined,
      groupUuids: filterValues.GROUPS.length ? filterValues.GROUPS : undefined,
      pageNum,
      missingInfoTypes: filterValues.MISSING_INFO_TYPE.length
        ? filterValues.MISSING_INFO_TYPE
        : undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const tableData = useMemo(
    () => buildTableData(data?.results || []),
    [data?.results]
  );

  const onAddMissingInfo = (row: MissingInfoRow) => {
    window.open(
      `/admin/crm/${row.groupUuid}/entity/${row.entityUuid}`,
      "_blank"
    );
  };

  const table = useReactTable({
    data: tableData,
    columns: buildColumns(onAddMissingInfo),
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 5 },
  });

  const hasGroupFilter = filterValues.GROUPS.length > 0;
  const hasMissingInfoTypeFilter = filterValues.MISSING_INFO_TYPE.length > 0;

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <DashboardContainer.Header sticky>
        <div className="t-flex t-items-center t-gap-3">
          <Search
            placeholder="Search entities..."
            onChange={(e) => {
              setPageNum(1);
              setSearchInput(e.target.value);
            }}
            className="t-w-[18%] focus:t-w-3/6 t-transition-all"
          />
          <Filter.Root
            defaultValue="group"
            capsule={
              <>
                {hasGroupFilter && (
                  <Chip
                    filterType="group"
                    isActive
                    onRemove={() => updateFilter("GROUPS", [])}
                  >
                    {`Group (${filterValues.GROUPS.length})`}
                  </Chip>
                )}
                {hasMissingInfoTypeFilter && (
                  <Chip
                    filterType="missing_info_type"
                    isActive
                    onRemove={() => updateFilter("MISSING_INFO_TYPE", [])}
                  >
                    {`Missing Info Type (${filterValues.MISSING_INFO_TYPE.length})`}
                  </Chip>
                )}
              </>
            }
          >
            <Filter.Portal>
              <Filter.List>
                <Filter.ListItem value="group">Company group</Filter.ListItem>
                <Filter.ListItem value="missing_info_type">
                  Missing Info Type
                </Filter.ListItem>
              </Filter.List>
              <Filter.Body value="group" block>
                <MultiGroupFilter
                  values={filterValues}
                  updateFilter={(name, value) => updateFilter(name, value)}
                />
              </Filter.Body>
              <Filter.Body value="missing_info_type" block>
                <MultiSelectFilter
                  options={MISSING_INFO_OPTIONS}
                  selected={filterValues.MISSING_INFO_TYPE}
                  onChange={(values) =>
                    updateFilter("MISSING_INFO_TYPE", values as string[])
                  }
                  isSearchable={false}
                  isSelectAll={false}
                />
              </Filter.Body>
            </Filter.Portal>
          </Filter.Root>
          <div className="t-ml-auto">
            <PaginationHandler
              page_num={pageNum}
              per_page={data?.per_page || 20}
              total_count={data?.total_count || 0}
              total_pages={data?.total_pages || 1}
              onPageNumChange={setPageNum}
              isLoading={isLoading}
            />
          </div>
        </div>
      </DashboardContainer.Header>

      {!isLoading && tableData.length === 0 ? (
        <div className="t-flex t-flex-col t-items-center t-justify-center t-h-72 t-text-text-60 t-gap-3">
          <p className="t-m-0 t-text-subtitle-sm">No missing info found</p>
          <p className="t-m-0 t-text-body-sm">
            All entities have the required information.
          </p>
        </div>
      ) : (
        <Table.Container isLoading={isLoading} sticky>
          <Table.Content>
            <Table.Head>
              <Table.HeadRow>
                {table.getFlatHeaders().map((header) => (
                  <Table.HeadCell key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Table.HeadCell>
                ))}
              </Table.HeadRow>
            </Table.Head>
            <Table.Body>
              {table.getRowModel().rows.map((row) => (
                <Table.Row key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      )}
    </div>
  );
};
